import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from "react";
import { ArrowLeft } from "lucide-react";
import { ResizableDialog, ResizableDialogContent } from "../resizable-dialog/ResizableDialog";
import { DialogShellHeader } from "../dialog-shell/DialogShellHeader";
import { DialogShellBody } from "../dialog-shell/DialogShellBody";
import { DialogShellFooter } from "../dialog-shell/DialogShellFooter";
import { Button } from "../../base/button/button";
import { EntityPickerBody } from "./EntityPickerBody";
import { getCandidateItems, isBucketViewAvailable } from "./useEntityPickerState";
const PICKER_SIZES = {
    compact: {
        width: 660,
        minHeight: 720
    },
    expanded: {
        width: 880,
        minHeight: 800
    }
};
export function EntityPickerModal({ open, onOpenChange, items, selectedIds, mode, onConfirm, config, suggestNew, onBack, excludeIds, isLoading }) {
    // Invariant: state declared in *this wrapper* outlives close — Radix unmounts
    // the dialog content, but this component keeps rendering — so every piece of
    // it MUST be reset on the open transition. Otherwise a canceled draft, the
    // previous target's selection when the parent retargets the modal (e.g. the
    // step editor switching steps), or a stale view mode leaks into the next
    // session. State living inside `EntityPickerBody` needs no such reset: Radix
    // unmounts it on close, so it re-initializes itself. While open, prop-driven
    // `selectedIds` additions merge into the draft so entities created
    // mid-session (e.g. from a "suggest new" flow) auto-check without an effect.
    const [draft, setDraft] = React.useState(selectedIds);
    const [userViewMode, setUserViewMode] = React.useState(null);
    const [prevOpen, setPrevOpen] = React.useState(open);
    const [prevSelectedIds, setPrevSelectedIds] = React.useState(selectedIds);
    if (prevOpen !== open) {
        setPrevOpen(open);
        if (open) {
            setPrevSelectedIds(selectedIds);
            setDraft(selectedIds);
            setUserViewMode(null);
        }
    } else if (open && prevSelectedIds !== selectedIds) {
        setPrevSelectedIds(selectedIds);
        const prevSet = new Set(prevSelectedIds);
        const additions = selectedIds.filter((id)=>!prevSet.has(id));
        if (additions.length > 0) {
            setDraft((d)=>{
                if (mode === "single") return [
                    additions[additions.length - 1]
                ];
                const existing = new Set(d);
                const toAdd = additions.filter((id)=>!existing.has(id));
                return toAdd.length > 0 ? [
                    ...d,
                    ...toAdd
                ] : d;
            });
        }
    }
    const bodyRef = React.useRef(null);
    // Mirrors EntityPickerBody's own decision (same helper, same post-excludeIds
    // candidate list) so this wrapper's sizing and "back to suggestions" footer
    // stay in sync: a picker with no recommendations opens straight into the
    // all-list (unless it has no items, where the bucket view's no-data empty
    // state is kept). Both derivations are reactive — `bucketViewAvailable`
    // depends on the async-loaded suggestions, so we can't freeze the view;
    // only the user's explicit navigation is sticky (see useEntityPickerState).
    const candidateItems = React.useMemo(()=>getCandidateItems(items, excludeIds), [
        items,
        excludeIds
    ]);
    const bucketViewAvailable = React.useMemo(()=>isBucketViewAvailable(config.bucketing, candidateItems), [
        config.bucketing,
        candidateItems
    ]);
    const autoViewMode = bucketViewAvailable ? "bucket" : "all";
    const viewMode = userViewMode ?? autoViewMode;
    const ctaLabel = mode === "single" ? config.copy.saveLabel : draft.length === 1 ? config.copy.primaryCtaSingular?.(1) ?? config.copy.saveLabel : config.copy.primaryCtaPlural?.(draft.length) ?? config.copy.saveLabel;
    const size = viewMode === "all" ? "expanded" : "compact";
    const showBackToBuckets = viewMode === "all" && bucketViewAvailable && !!config.copy.switchToBucketsLabel;
    return /*#__PURE__*/ _jsx(ResizableDialog, {
        open: open,
        onOpenChange: onOpenChange,
        children: /*#__PURE__*/ _jsxs(ResizableDialogContent, {
            sizes: PICKER_SIZES,
            size: size,
            hideAutoClose: true,
            className: "rounded-2xl bg-white p-0 gap-0 text-foreground shadow-2xl",
            onEscapeKeyDown: (e)=>{
                if (bodyRef.current?.getViewMode() === "all" && bucketViewAvailable) {
                    e.preventDefault();
                    bodyRef.current.backToBuckets();
                }
            },
            children: [
                /*#__PURE__*/ _jsx(DialogShellHeader, {
                    icon: config.headerIcon,
                    iconTone: config.headerIcon ? "green" : "neutral",
                    title: config.copy.title,
                    subtitle: config.copy.description,
                    onClose: ()=>onOpenChange(false)
                }),
                /*#__PURE__*/ _jsx(DialogShellBody, {
                    padding: "px-6 pt-5 pb-4",
                    children: /*#__PURE__*/ _jsx(EntityPickerBody, {
                        ref: bodyRef,
                        items: items,
                        selectedIds: draft,
                        mode: mode,
                        config: config,
                        suggestNew: suggestNew,
                        excludeIds: excludeIds,
                        isLoading: isLoading,
                        onDraftChange: setDraft,
                        onViewModeChange: setUserViewMode
                    })
                }),
                /*#__PURE__*/ _jsx(DialogShellFooter, {
                    hint: config.copy.footerSelectedSummary?.(draft.length),
                    leftSlot: showBackToBuckets ? /*#__PURE__*/ _jsxs(Button, {
                        type: "button",
                        variant: "ghost",
                        wrap: true,
                        onClick: ()=>bodyRef.current?.backToBuckets(),
                        children: [
                            /*#__PURE__*/ _jsx(ArrowLeft, {}),
                            config.copy.switchToBucketsLabel
                        ]
                    }) : undefined,
                    secondary: {
                        label: onBack ? config.copy.backLabel ?? config.copy.cancelLabel : config.copy.cancelLabel,
                        onClick: ()=>onBack ? onBack() : onOpenChange(false)
                    },
                    primary: {
                        label: ctaLabel,
                        onClick: ()=>onConfirm(draft)
                    }
                })
            ]
        })
    });
}

//# sourceMappingURL=EntityPickerModal.js.map